/**
 * Estimate formatting shared across the backlog row and the breakdown review
 * panel (FLOW-549). The t-shirt ladder mirrors the backend's
 * `T_SHIRT_SIZE_LADDER` (backend/src/ai/breakdown/generate.rs) — the server
 * snaps every t-shirt estimate onto one of these rungs before it reaches the
 * client, so the frontend only needs the label lookup, never nearest-rung math.
 */

import type { EstimateUnit } from "../api/types";

const T_SHIRT_LABELS: Record<number, string> = {
  1: "XS",
  2: "S",
  3: "M",
  5: "L",
  8: "XL",
};

/**
 * The ladder itself, ascending — integer-like keys enumerate in numeric order.
 * Derived from the label map so the two can never disagree.
 */
export const T_SHIRT_LADDER: number[] = Object.keys(T_SHIRT_LABELS).map(Number);

/** Label for a t-shirt-sized value; falls back to the raw number off the ladder. */
export function tShirtLabel(value: number): string {
  return T_SHIRT_LABELS[value] ?? `${value}`;
}

/**
 * The suffix each numeric unit is written with. `t_shirt_size` has none — its
 * ladder labels (XS…XL) are the unit — so it is excluded rather than mapped to
 * an empty string, which keeps `formatEstimate` and the detail rail's static
 * suffix (FLOW-909) from having to treat "" as a real label.
 */
export const UNIT_SUFFIX: Record<
  Exclude<EstimateUnit, "t_shirt_size">,
  string
> = {
  story_points: "pts",
  hours: "h",
  days: "d",
};

/** `{value} {unit}` formatting for the breakdown review panel (FLOW-549). */
export function formatEstimate(value: number, unit: EstimateUnit): string {
  if (unit === "t_shirt_size") return tShirtLabel(value);
  // Widened on purpose, standing in for the old switch's `default` arm:
  // `EstimateUnit` is a closed union but the value arrives from the API
  // unvalidated, and a unit this map has not heard of must degrade to the bare
  // number rather than render "5undefined".
  const suffix: string | undefined = UNIT_SUFFIX[unit];
  if (suffix === undefined) return `${value}`;
  // "5 pts" reads as a count; hours and days are written tight — "5h", "5d".
  const separator = unit === "story_points" ? " " : "";
  return `${value}${separator}${suffix}`;
}

/**
 * A total across several estimates, or null when the unit has no meaningful
 * sum. T-shirt sizes are ordinal: stories at M, L and XL do not add up to a
 * size, and adding their ladder rungs (3 + 5 + 8) yields 16 — a number off
 * the ladder that would render as a bare "16" beside real sizes. Callers show
 * the story count alone in that case.
 */
export function formatEstimateTotal(
  value: number,
  unit: EstimateUnit,
): string | null {
  return unit === "t_shirt_size" ? null : formatEstimate(value, unit);
}
